import { TableColumnsProp } from 'src/app/shared/models/tables.model';
import { SetRangeGridContent } from './number-ranges.model';
import { FormConfig } from 'src/app/shared/models/dynamic.model';
import {
  BulkDeleteItemNames,
  GridColumnsStorageNames,
  gridMessages,
  MessageTemplates,
  searchDataParam,
} from 'src/app/shared/utility/constants';
import { DataTableConfig } from 'src/app/shared/components/data-table';
import { NumberRangesGridContent } from './number-ranges.model';

const isSelectedAndDefaultView = {
  isSelected: true,
  defaultView: true,
};

export interface NumberRangeSummarySearchCriteria {
  searchText: string;
}

export const numberRangesGridColumnConfig: TableColumnsProp[] = [
  {
    ...isSelectedAndDefaultView,
    key: 'numberRangeTypeName',
    label: 'Number Range Type',
     defaultPrintWidth: 80,
  },
  {
    key: 'seriesName',
    ...isSelectedAndDefaultView,
    label: 'Series Name',
    defaultPrintWidth: 150
  },
  {
    ...isSelectedAndDefaultView,
    label: 'Prefix',
    key: 'prefix',
     defaultPrintWidth: 80,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'facility',
    label: 'Facility',
     defaultPrintWidth: 150,
  },
  {
    ...isSelectedAndDefaultView,
    label: 'Range(s)',
    key: 'setRange',
     defaultPrintWidth: 300,
  },
  {
    key: 'additionalInformation',
    label: 'Additional Information',
    isSelected: false,
    defaultView: false,
     defaultPrintWidth: 150,
  },
];

export const numberRangeDataTableConfig: DataTableConfig<NumberRangesGridContent> =
  {
    rowIdField: 'id',
    entityName: BulkDeleteItemNames.NumberRanges,
    storageKey: GridColumnsStorageNames.smNumberRanges,
    columns: [
      {
        key: 'numberRangeTypeName',
        label: 'Number Range Type',
        isSelected: true,
        defaultView: true,
        sortable: true,
        defaultPrintWidth: 80,
      },
      {
        key: 'seriesName',
        label: 'Series Name',
        isSelected: true,
        defaultView: true,
        sortable: true,
        isHyperlink: true,
        defaultPrintWidth: 150,
      },
      {
        key: 'prefix',
        label: 'Prefix',
        isSelected: true,
        defaultView: true,
        sortable: true,
        defaultPrintWidth: 80,
      },
      {
        key: 'facility',
        label: 'Facility',
        isSelected: true,
        defaultView: true,
        sortable: true,
        defaultPrintWidth: 150,
        showTooltip: true,
      },
      {
        key: 'setRange',
        label: 'Range(s)',
        isSelected: true,
        defaultView: true,
        sortable: false,
        isHyperlink: true,
        /** Same formatting as summary-table `customiseTableDataDisplay` for `setRange`. */
        displayFn: row => {
          const ranges = row.setRange;
          if (!Array.isArray(ranges)) {
            return '';
          }
          return `View Range${ranges.length === 1 ? '' : 's'} (${ranges.length})`;
        },
        defaultPrintWidth: 300,
      },
      {
        key: 'additionalInformation',
        label: 'Additional Information',
        isSelected: false,
        defaultView: false,
        sortable: true,
        defaultPrintWidth: 150,
      },
    ],
    features: {
      selection: true,
      pagination: true,
      sorting: true,
      columnReorder: true,
      selectAllAcrossPages: true,
    },
    messages: {
      noSearch: gridMessages.noNumberRangeSearch,
      noMatch: gridMessages.noRecordsMatch(BulkDeleteItemNames.NumberRanges),
      allPageSelected: MessageTemplates.AllItemsSelected,
      selectAll: MessageTemplates.SelectAllItems,
    },
    defaultPagination: {
      pageSize: 10,
      sortField: 'id',
      sortDirection: 'desc',
    },
  };

export const numberRangesInfoFormFieldsConfig: FormConfig = {
  fields: [
    {
      type: 'select',
      id: 'numberRangeTypeName',
      required: true,
      label: 'Number Range Type',
      requiredError: 'Number Range Type is required',
    },
    {
      label: 'Number Range Type Id',
      type: 'extra',
      id: 'numberRangeType',
    },
    {
      type: 'input',
      id: 'number',
      label: 'Number',
      minValue: 1,
      maxValue: 1,
      allowNumeric: true,
    },
    {
      id: 'seriesName',
      type: 'input',
      label: 'Series Name',
      required: true,
      requiredError: 'Series Name is required',
      minValue: 1,
      maxValue: 126,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'prefix',
      type: 'input',
      label: 'Prefix',
      required: true,
      minValue: 1,
      maxValue: 126,
      allowAlphaNumeric: true,
      requiredError: 'Prefix is required',
    },
    {
      id: 'facility',
      type: 'input',
      label: 'Facility',
    },
    {
      label: 'Facility Id',
      type: 'extra',
      id: 'facilityId',
    },
    {
      id: 'additionalInformation',
      type: 'text',
      label: 'Additonal Information',
      maxValue: 500,
      noInitialWhiteSpace: true,
      isCheckForMaxValue: true,
    },
    {
      label: 'id',
      type: 'extra',
      id: 'id',
    },
  ],
};

export const defaultNumberRangePagination = {
  sortingOrder: searchDataParam.desc,
  sortingField: searchDataParam.id,
  fromIndex: 0,
  toIndex: 10,
};

export const orderDropdownValues = [
  {
    id: 1,
    name: 'Start Position',
    isSystemMessage: false,
  },
  {
    id: 2,
    name: 'Middle Position',
    isSystemMessage: true,
  },
  {
    id: 2,
    name: 'Last Position',
    isSystemMessage: true,
  },
];

export const numberRangesInfoSetRangeTable: TableColumnsProp[] = [
  {
    ...isSelectedAndDefaultView,
    label: 'Start Number',
    key: 'startNumber',
  },
  {
    label: 'End Number',
    ...isSelectedAndDefaultView,
    key: 'endNumber',
  },
  {
    key: 'lastUsedNumber',
    ...isSelectedAndDefaultView,
    label: 'Last Used Number',
  },
  {
    key: 'priority',
    ...isSelectedAndDefaultView,
    label: 'Priority',
  },
];

export const setRangeFormConfig: FormConfig = {
  fields: [
    {
      id: 'startNumber',
      type: 'input',
      required: true,
      minValue: 1,
      maxValue: 9,
      allowNumberWithFirstNonZeroNumber: true,
      requiredError: 'Start Number is required',
    },
    {
      id: 'endNumber',
      type: 'input',
      required: true,
      minValue: 1,
      maxValue: 9,
      allowNumberWithFirstNonZeroNumber: true,
      requiredError: 'End Number is required',
    },
    {
      id: 'lastUsedNumber',
      type: 'input',
    },
    {
      id: 'priority',
      type: 'input',
      required: true,
      minValue: 1,
      maxValue: 2,
      allowNumberWithFirstNonZeroNumber: true,
      requiredError: 'Priority is required',
    },
    {
      label: 'Id',
      type: 'extra',
      id: 'id',
    },
    {
      label: 'Temp Id',
      type: 'extra',
      id: 'tempId',
    },
    {
      label: 'Action',
      type: 'extra',
      id: 'action',
    },
    {
      id: 'isInputRow',
      type: 'extra',
    },
  ],
};

export const setRangeInputRowData: SetRangeGridContent[] = [
  {
    startNumber: '',
    endNumber: '',
    lastUsedNumber: '',
    priority: '',
    isInputRow: true,
  },
];

export const NumberRangesExcelHeaders: string[] = [
  'Number Range Type',
  'Series Name',
  'Prefix',
  'Facility',
  'Additional Information',
  'Range(s)',
];

/** Must stay in 1:1 order with NumberRangesExcelHeaders — not NumberRangesConfigColumnList (grid has select/action). */
export const numberRangesExcelColumnList: string[] = [
  'numberRangeTypeName',
  'seriesName',
  'prefix',
  'facility',
  'additionalInformation',
  'setRange',
];

export const NumberRangeSectionHeadersName: string[] = [
  'Number Range Details',
  'Range(s)',
];

export const NumberRangeSectionHeadersNameKey: string[] = [
  'numberRangeDetails',
  'numberRanges',
];

